import type {
    IndexedDevices,
    InputConstraintSet,
    MediaDeviceInfoLike,
    MediaDeviceRequest,
} from '@pexip/media-control';
import {
    hasChangedInput,
    isMediaDeviceInfo,
    mergeConstraints,
    reverseDeviceId,
} from '@pexip/media-control';
import type {AsyncQueueOptions} from '@pexip/utils';
import {createAsyncQueue, assert} from '@pexip/utils';

import type {
    ExtendedMediaTrackSettings,
    ExtendedMediaTrackSettingsKey,
    Media,
    MediaSignals,
    MediaTrack,
    TrackProcessor,
    Unsubscribe,
} from './types';
import {
    AUDIO_SETTINGS_KEYS,
    VIDEO_SETTINGS_KEYS,
    createMediaProcessor,
    hasSettingsChanged,
    isTrackEnded,
    isTrackMuted,
} from './utils';
import {isMedia} from './typeGuard';
import {createModuleLogger} from './logger';
import {
    createGetUserMediaProcess,
    requestUserMediaWithRetry,
} from './userMedia';
import {createMediaUpdater} from './media';

const DEFAULT_QUEUE_DELAY_MS = 100;
const DEFAULT_QUEUE_DROP_LAST = false;
const DEFAULT_QUEUE_SIZE = 1;
const DEFAULT_QUEUE_THROTTLE_MS = 500;

type EventCallback<T> = (event: T) => void;
type EventErrorCallback = (error: Error) => void;

export type PreviewInput = MediaDeviceInfoLike | undefined;

export interface PreviewEventHandler {
    audioInput?: EventCallback<PreviewInput>;
    videoInput?: EventCallback<PreviewInput>;
    media?: EventCallback<Media>;

    videoInputError?: EventErrorCallback;
    audioInputError?: EventErrorCallback;
    applyChangesError?: EventErrorCallback;
    revertChangesError?: EventErrorCallback;

    audioMuted?: EventCallback<boolean | undefined>;
    audioSuspended?: EventCallback<boolean | undefined>;
    updatingMain?: EventCallback<boolean>;
    updatingPreviewAudio?: EventCallback<boolean>;
    updatingPreviewVideo?: EventCallback<boolean>;
    videoMuted?: EventCallback<boolean | undefined>;
    videoSuspended?: EventCallback<boolean | undefined>;

    unsubscribeMain?: Unsubscribe;
}

export interface PreviewStreamParams {
    getCurrentDevices: () => IndexedDevices;
    getCurrentMedia: () => Media | undefined;
    updateMainStream: (request: MediaDeviceRequest) => Promise<void>;
    mainMediaSignal: MediaSignals['onMediaChanged'];
    signals: MediaSignals;
    onEnded?: () => void;
    fftSize?: number;
    queueOptions?: Partial<AsyncQueueOptions>;
    audioProcessors: TrackProcessor[];
    videoProcessors: TrackProcessor[];
    /**
     * Pass default constraints to use with get media wrappers
     */
    getDefaultConstraints?: () => {
        audio?: InputConstraintSet | false;
        video?: InputConstraintSet | false;
    };
}

export interface PreviewControllerProps {
    media: Media | undefined;
    audioInput?: MediaDeviceInfoLike;
    videoInput?: MediaDeviceInfoLike;
    updatingPreviewAudio: boolean;
    updatingPreviewVideo: boolean;
    updatingMain: boolean;
    originalMainAudioInput?: MediaDeviceInfoLike;
    discardMedia: boolean;
    initialized: boolean;
    signals: MediaSignals;
}

export interface PreviewStreamController {
    // Props
    media: Media | undefined;
    audioInputChanged: boolean;
    videoInputChanged: boolean;
    inputChanged: boolean;
    audioInput: PreviewInput;
    videoInput: PreviewInput;
    updatingMain: boolean;
    updatingPreviewAudio: boolean;
    updatingPreviewVideo: boolean;

    // Methods
    updatePreviewInput(input: PreviewInput): void;
    updateAudioInput(id: string): void;
    updateVideoInput(id: string): void;
    applyChanges(force?: boolean): Promise<void>;
    revertChanges(): Promise<void>;
    cleanup: () => Promise<void>;

    // Events
    onMediaChanged(callback: EventCallback<Media>): Unsubscribe;
    onAudioInputChanged(callback: EventCallback<PreviewInput>): Unsubscribe;
    onVideoInputChanged(callback: EventCallback<PreviewInput>): Unsubscribe;
    onUpdatingPreviewAudio(callback: EventCallback<boolean>): Unsubscribe;
    onUpdatingPreviewVideo(callback: EventCallback<boolean>): Unsubscribe;
    onUpdatingMain(callback: EventCallback<boolean>): Unsubscribe;
    onAudioInputError(callback: EventErrorCallback): Unsubscribe;
    onVideoInputError(callback: EventErrorCallback): Unsubscribe;
    onApplyChangesError(callback: EventErrorCallback): Unsubscribe;
    onRevertChangesError(callback: EventErrorCallback): Unsubscribe;
    onAudioMuted(callback: EventCallback<boolean | undefined>): Unsubscribe;
    onAudioSuspended(callback: EventCallback<boolean | undefined>): Unsubscribe;
    onVideoMuted(callback: EventCallback<boolean | undefined>): Unsubscribe;
    onVideoSuspended(callback: EventCallback<boolean | undefined>): Unsubscribe;
}

const isPreviewInput = (value: unknown): value is PreviewInput => {
    if (isMediaDeviceInfo(value) || value === undefined) {
        return true;
    }
    return false;
};

const extractFeaturesToConstraints = (
    keysToLookFor: ExtendedMediaTrackSettingsKey[],
    settings: ExtendedMediaTrackSettings | undefined,
) => {
    if (!settings) {
        return {};
    }
    return keysToLookFor.reduce<InputConstraintSet>((set, key) => {
        if (settings[key] !== undefined) {
            // @ts-expect-error performance
            set[key] = settings[key];
        }
        return set;
    }, {});
};

const createEventHandler =
    (eventHandlers: PreviewEventHandler) =>
    <T extends keyof PreviewEventHandler>(key: T) =>
    (callback: PreviewEventHandler[T]): Unsubscribe => {
        eventHandlers[key] = callback;
        return () => {
            eventHandlers[key] = undefined;
        };
    };

type GetMedia<T = Media | undefined> = () => T;
const createAudioVideoProcessingSettingsChangeDetector = (
    getCurrentMedia: GetMedia<Media | undefined>,
    getPreviewMedia: GetMedia,
) => {
    const hasAudioSettingsChanged = hasSettingsChanged(AUDIO_SETTINGS_KEYS);
    const hasVideoSettingsChanged = hasSettingsChanged(VIDEO_SETTINGS_KEYS);
    return () => {
        const currentMedia = getCurrentMedia?.();
        const previewMedia = getPreviewMedia();

        const {audio: mainAudio, video: mainVideo} =
            currentMedia?.getSettings() ?? {audio: {}, video: {}};
        const {audio: previewAudio, video: previewVideo} =
            previewMedia?.getSettings() ?? {audio: {}, video: {}};
        const changed =
            hasAudioSettingsChanged(mainAudio, previewAudio) ||
            hasVideoSettingsChanged(mainVideo, previewVideo);
        return changed;
    };
};

export const createPreviewStreamController = ({
    getCurrentDevices,
    getCurrentMedia,
    updateMainStream,
    onEnded,
    mainMediaSignal,
    queueOptions = {
        size: DEFAULT_QUEUE_SIZE,
        throttleInMS: DEFAULT_QUEUE_THROTTLE_MS,
        delayInMS: DEFAULT_QUEUE_DELAY_MS,
        dropLast: DEFAULT_QUEUE_DROP_LAST,
    },
    audioProcessors,
    videoProcessors,
    getDefaultConstraints = () => ({}),
    signals,
}: PreviewStreamParams): PreviewStreamController => {
    const queue = createAsyncQueue(queueOptions);
    const eventHandlers: PreviewEventHandler = {};
    const internalProps: PreviewControllerProps = {
        media: undefined,
        updatingPreviewAudio: false,
        updatingPreviewVideo: false,
        updatingMain: false,
        discardMedia: false,
        initialized: false,
        signals,
    };

    const logger = createModuleLogger({
        module: 'PreviewStreamController',
        eventHandlers,
        props: internalProps,
    });

    const cleanUpMainSubscription = () => {
        if (eventHandlers.unsubscribeMain) {
            eventHandlers.unsubscribeMain();
            eventHandlers.unsubscribeMain = undefined;
        }
    };

    type InternalProps = typeof internalProps;
    const props = new Proxy(internalProps, {
        get: (target, p: keyof InternalProps) => {
            return target[p];
        },
        set: (target, p: keyof InternalProps, value) => {
            if (target[p] === value) {
                return true;
            }
            logger.debug(
                {oldValue: target[p], newValue: value as unknown},
                `Update Props[${p}]`,
            );
            switch (p) {
                case 'media': {
                    if (!isMedia(value)) {
                        return false;
                    }
                    target[p] = value;
                    eventHandlers[p]?.(value);
                    // Unsubscribe main stream signal on change of inputs
                    cleanUpMainSubscription();
                    return true;
                }
                case 'audioInput':
                case 'videoInput': {
                    if (!isPreviewInput(value)) {
                        return false;
                    }
                    target[p] = value;
                    eventHandlers[p]?.(value);
                    return true;
                }
                case 'updatingMain':
                case 'updatingPreviewVideo':
                case 'updatingPreviewAudio': {
                    if (typeof value !== 'boolean') {
                        return false;
                    }
                    target[p] = value;
                    eventHandlers[p]?.(value);
                    return true;
                }
                default:
                    Reflect.set(target, p, value);
                    return true;
            }
        },
    });

    const subscriptions = [
        props.signals.onMediaTrackMuted?.add(track => {
            if (track.kind === 'audioinput') {
                eventHandlers.audioMuted?.(track.muted);
            } else {
                eventHandlers.videoMuted?.(track.muted);
            }
        }),
        props.signals.onMediaTrackSuspended?.add(track => {
            if (track.kind === 'audioinput') {
                eventHandlers.audioSuspended?.(track.suspended);
            } else {
                eventHandlers.videoSuspended?.(track.suspended);
            }
        }),
        props.signals.onMediaTrackResumed?.add(track => {
            if (track.kind === 'audioinput') {
                eventHandlers.audioSuspended?.(track.suspended);
            } else {
                eventHandlers.videoSuspended?.(track.suspended);
            }
        }),
        props.signals.onAddTrack?.add(track => {
            switch (track.kind) {
                case 'audio': {
                    eventHandlers.audioMuted?.(
                        isTrackMuted(track) || isTrackEnded(track),
                    );
                    break;
                }
                case 'video': {
                    eventHandlers.videoMuted?.(
                        isTrackMuted(track) || isTrackEnded(track),
                    );
                    break;
                }
            }
        }),
        props.signals.onRemoveTrack?.add(track => {
            switch (track.kind) {
                case 'audio': {
                    eventHandlers.audioMuted?.(
                        isTrackMuted(track) || isTrackEnded(track),
                    );
                    break;
                }
                case 'video': {
                    eventHandlers.videoMuted?.(
                        isTrackMuted(track) || isTrackEnded(track),
                    );
                    break;
                }
            }
        }),
    ];

    const getUserMedia = createGetUserMediaProcess({
        getUserMedia: requestUserMediaWithRetry(() =>
            Promise.resolve(getCurrentDevices()),
        ),
        signals: props.signals,
        getCurrentDevices: () => Promise.resolve(getCurrentDevices()),
        stopVideoTrackAsMute: () => false,
        updateMedia: constraints => updateMedia(constraints),
    });

    const mergeMediaConstraints = (constraints: MediaDeviceRequest) => {
        const {audio, video} = getDefaultConstraints();
        return {
            audio:
                audio === false
                    ? false
                    : mergeConstraints(audio)(constraints.audio),
            video:
                video === false
                    ? false
                    : mergeConstraints(video)(constraints.video),
        };
    };

    const processMedia = createMediaProcessor({
        audioProcessors,
        videoProcessors,
        onProcessingError(error, track) {
            logger.error({error, track}, 'Failed to process media track');
        },
    });

    const processAndUpdateMedia = async (
        media: Media,
        tracks: MediaTrack[],
        sync = false,
    ) => {
        try {
            const processedTracks = await processMedia(tracks);
            for (const [idx, track] of processedTracks.entries()) {
                const originTrack = tracks.at(idx);
                assert(
                    originTrack,
                    'Processed track should always has the original track in the same order',
                );
                // Only replace track when they are not the same
                if (originTrack.id !== track.id) {
                    if (sync) {
                        if (originTrack.track && track.track) {
                            track.track.enabled = originTrack.track.enabled;
                        }
                    }
                    media.removeTrack(originTrack);
                    media.addTrack(track);
                }
            }
        } catch (error: unknown) {
            logger.error({error, tracks}, 'Failed to process media tracks');
        }
    };

    const updateMediaProcess = createMediaUpdater({
        getUserMedia,
        signals: props.signals,
        getDefaultConstraints,
        getCurrentDevices: () => Promise.resolve(getCurrentDevices()),
        shouldDiscardMedia: () => props.discardMedia,
        onMediaTracksChanged: (media, tracks) => {
            props.media = media;
            queue.enqueue(async () => {
                await processAndUpdateMedia(media, tracks, false);
            });
        },
    });

    const updateMedia = async (constraints: MediaDeviceRequest) => {
        await updateMediaProcess(
            mergeMediaConstraints(constraints),
            props.media,
        );
    };

    const initFromMain = (mainMedia: Media | undefined) => {
        if (!mainMedia?.stream) {
            return;
        }
        try {
            const clonedMedia = mainMedia.clone(props.signals, 'Preview');
            props.media = clonedMedia;
            queue.enqueue(async () => {
                await processAndUpdateMedia(
                    clonedMedia,
                    clonedMedia.getTracks(),
                    true,
                );
            });
            props.audioInput = mainMedia.audioInput;
            props.videoInput = mainMedia.videoInput;
            props.originalMainAudioInput = mainMedia.audioInput;
        } catch (error: unknown) {
            logger.error({error}, 'Failed to post process media');
        } finally {
            props.initialized = true;
            if (props.discardMedia) {
                void props.media?.release();
            }
        }
    };

    const mainMedia = getCurrentMedia();
    if (mainMedia?.stream) {
        initFromMain(mainMedia);
    } else {
        eventHandlers.unsubscribeMain = mainMediaSignal.add(initFromMain);
    }

    const replaceMainStream = async (constraints: MediaDeviceRequest) => {
        logger.debug({constraints}, 'Replacing main stream');
        props.updatingMain = true;
        await updateMainStream(constraints);
        props.updatingMain = false;
    };

    const updatePreviewMedia = async (constraints: MediaDeviceRequest) => {
        logger.debug({constraints}, 'Requesting a new preview stream');
        await updateMedia(constraints);
        logger.debug({media: props.media}, 'Preview media updated');
    };

    const releaseAudio = async () => {
        for (const track of props.media?.getAudioTracks() ?? []) {
            await track.release();
        }
    };
    const releaseVideo = async () => {
        for (const track of props.media?.getVideoTracks() ?? []) {
            await track.release();
        }
    };

    const updateAudioInput = async (input: PreviewInput) => {
        const request = {audio: {device: {exact: input}}};
        try {
            props.audioInput = input;
            props.updatingPreviewAudio = true;
            if (props.audioInput === undefined) {
                return await releaseAudio();
            }
            await updatePreviewMedia(request);
        } catch (error: unknown) {
            if (error instanceof Error) {
                const errorName =
                    error.name === 'Error' ? error.message : error.name;
                if (errorName === 'NotReadableError') {
                    try {
                        logger.debug(
                            {input},
                            'NotReadableError, trying to sync main stream AudioInput',
                        );
                        await replaceMainStream({
                            audio: input,
                            video: getCurrentMedia()?.videoInput,
                        });
                        await updatePreviewMedia(request);
                    } catch (err: unknown) {
                        logger.warn(
                            {error: err, input},
                            'Unable to recover NotReadableError AudioInput for preview',
                        );
                        if (err instanceof Error) {
                            return eventHandlers.audioInputError?.(err);
                        }
                        throw err;
                    }
                } else {
                    logger.warn(
                        {error, input},
                        'Unable to update AudioInput for preview',
                    );
                    eventHandlers.audioInputError?.(error);
                }
            } else {
                logger.error(
                    {error, input},
                    'Unable to update AudioInput for preview',
                );
                throw error;
            }
        } finally {
            props.updatingPreviewAudio = false;
        }
    };

    const updateVideoInput = async (input: PreviewInput) => {
        try {
            props.videoInput = input;
            props.updatingPreviewVideo = true;
            if (input === undefined) {
                return await releaseVideo();
            }
            return await updatePreviewMedia({video: {device: {exact: input}}});
        } catch (error: unknown) {
            if (error instanceof Error) {
                logger.warn(
                    {error, input},
                    'Unable to update VideoInput for preview',
                );
                return eventHandlers.videoInputError?.(error);
            }
            throw error;
        } finally {
            props.updatingPreviewVideo = false;
        }
    };

    const cleanup = async () => {
        logger.debug('Cleanup preview controller');
        await props.media?.release();
        cleanUpMainSubscription();
        for (const unsub of subscriptions) {
            unsub?.();
        }
        subscriptions.length = 0;
        props.audioInput = undefined;
        props.videoInput = undefined;
        onEnded?.();
    };

    const hasChangedAudioInput = () =>
        hasChangedInput(props.originalMainAudioInput, props.audioInput);

    const hasChangedVideoInput = () =>
        hasChangedInput(getCurrentMedia()?.videoInput, props.videoInput);

    const hasAudioVideoProcessingSettingsChanged =
        createAudioVideoProcessingSettingsChangeDetector(
            getCurrentMedia,
            () => props.media,
        );

    const hasChanges = () =>
        hasChangedAudioInput() ||
        hasChangedVideoInput() ||
        hasAudioVideoProcessingSettingsChanged();

    const applyChanges = async (force = false) => {
        const {audioInput, videoInput} = props;
        const {audio: previewAudio, video: previewVideo} =
            props.media?.getSettings() ?? {audio: {}, video: {}};

        if (force || hasChanges()) {
            const audioSettings = extractFeaturesToConstraints(
                AUDIO_SETTINGS_KEYS,
                previewAudio,
            );
            // Omit the width & height settings from the preview so that the one
            // from the main can be applied
            const {
                width: _width,
                height: _height,
                ...videoSettings
            } = extractFeaturesToConstraints(VIDEO_SETTINGS_KEYS, previewVideo);
            logger.info(
                {audioInput, videoInput, audioSettings, videoSettings},
                'Apply changes to main',
            );
            try {
                await replaceMainStream({
                    audio: audioInput
                        ? {device: {exact: audioInput}, ...audioSettings}
                        : false,
                    video: videoInput
                        ? {device: {exact: videoInput}, ...videoSettings}
                        : false,
                });
                props.originalMainAudioInput = props.audioInput;
            } catch (error: unknown) {
                if (error instanceof Error) {
                    logger.warn({error}, 'Unable to apply changes to main');
                    return eventHandlers.applyChangesError?.(error);
                }
                throw error;
            }
        }
    };

    const revertChanges = async () => {
        const mainMedia = getCurrentMedia();
        const mainStream = mainMedia?.stream;
        if (mainMedia && mainStream) {
            // Workaround for iOS Safari bug
            // https://bugs.webkit.org/show_bug.cgi?id=179363
            // https://github.com/pexip/mcu/issues/43138
            const mainMuted = !!mainStream.getTracks().some(t => t.muted);
            const current = mainMedia.audioInput;
            const prev = props.originalMainAudioInput;
            const audioChanged = hasChangedInput(prev, current);

            if (mainMuted || audioChanged) {
                logger.info(
                    {
                        mainMuted,
                        audioChanged,
                        currentAudioInput: current,
                        prevAudioInput: prev,
                        mainMedia,
                    },
                    'Reverting Changes to main',
                );
                try {
                    await replaceMainStream({
                        audio: prev,
                        video: getCurrentMedia()?.videoInput,
                    });
                } catch (error: unknown) {
                    if (error instanceof Error) {
                        logger.warn(
                            {error},
                            'Unable to revert changes to main',
                        );
                        return eventHandlers.revertChangesError?.(error);
                    }
                    throw error;
                }
            }
        }
    };

    const updateInput =
        (
            hasChanged: (input: PreviewInput) => boolean,
            updater: (input: PreviewInput) => Promise<void>,
        ) =>
        (id: string) => {
            const [input] = id
                ? getCurrentDevices().get(reverseDeviceId(id))
                : [];
            if (hasChanged(input)) {
                queue.enqueue(async () => {
                    await updater(input);
                });
            }
        };

    const toEvenHandler = createEventHandler(eventHandlers);

    return {
        get media() {
            return props.media;
        },

        get audioInputChanged() {
            return hasChangedAudioInput();
        },

        get videoInputChanged() {
            return hasChangedVideoInput();
        },

        get inputChanged() {
            return hasChanges();
        },

        get audioInput() {
            return props.audioInput;
        },

        get videoInput() {
            return props.videoInput;
        },

        get updatingPreviewAudio() {
            return props.updatingPreviewAudio;
        },

        get updatingPreviewVideo() {
            return props.updatingPreviewVideo;
        },

        get updatingMain() {
            return props.updatingMain;
        },

        updatePreviewInput: input => {
            switch (input?.kind) {
                case 'audioinput': {
                    return updateAudioInput(input);
                }
                case 'videoinput': {
                    return updateVideoInput(input);
                }
            }
        },

        updateAudioInput: updateInput(
            input =>
                props.initialized && hasChangedInput(props.audioInput, input),
            updateAudioInput,
        ),

        updateVideoInput: updateInput(
            input =>
                props.initialized && hasChangedInput(props.videoInput, input),
            updateVideoInput,
        ),

        onMediaChanged: toEvenHandler('media'),
        onAudioInputChanged: toEvenHandler('audioInput'),
        onVideoInputChanged: toEvenHandler('videoInput'),
        onAudioInputError: toEvenHandler('audioInputError'),
        onVideoInputError: toEvenHandler('videoInputError'),
        onApplyChangesError: toEvenHandler('applyChangesError'),
        onRevertChangesError: toEvenHandler('revertChangesError'),
        onUpdatingPreviewAudio: toEvenHandler('updatingPreviewAudio'),
        onUpdatingPreviewVideo: toEvenHandler('updatingPreviewVideo'),
        onUpdatingMain: toEvenHandler('updatingMain'),
        onAudioMuted: toEvenHandler('audioMuted'),
        onVideoMuted: toEvenHandler('videoMuted'),
        onAudioSuspended: toEvenHandler('audioSuspended'),
        onVideoSuspended: toEvenHandler('videoSuspended'),

        applyChanges,
        revertChanges,
        cleanup,
    };
};

export type CreatePreviewStreamController =
    typeof createPreviewStreamController;
